import { z } from 'zod';
/**
 * User — OIDC-backed identity, lazy-created on first login.
 * Spec §4.1, §4.3.
 *
 * `subject` is the OIDC `sub` claim, persisted from `users.subject`. It is
 * nullable — Ping's access token carries no stable `sub` today, so this is
 * `null` for every current login; see Unresolved Question #1 in the Ping
 * OIDC migration plan. ADO-Graph-backed features (org-membership probe,
 * admin/reviewer group sync) that key off this value are correspondingly
 * degraded for Ping-only users until IT emits a usable identifier.
 */
export const userSchema = z.object({
    id: z.string().uuid(),
    subject: z.string().nullable(),
    email: z.string().email(),
    displayName: z.string().nullable(),
    isTrusted: z.boolean().default(false),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    /**
     * Last authenticated sign-in. Written ONLY by the auth plugin's login
     * upsert, which is what separates it from `updatedAt` — roster reconcile,
     * ADO identity mapping and the trust toggle all bump the latter, so it is
     * not an activity signal. `null` for a user adopted by roster reconcile who
     * has never signed in; the admin Users column renders that as "Never",
     * never as the epoch.
     */
    lastLoginAt: z.string().datetime().nullable(),
    deletedAt: z.string().datetime().nullable(),
});
/** Summary reference used when embedding user refs in other payloads. */
export const userRefSchema = z.object({
    id: z.string().uuid(),
    displayName: z.string().nullable(),
    email: z.string().email(),
});
/**
 * Payload served by `GET /auth/me`.
 *
 * `isTrusted` is intentionally NOT on this payload — it lives on the
 * `userSchema` row (admin-facing) and is reloaded by the validate-submission
 * worker via its own DB JOIN. The principal hook never needs to gate UI
 * behaviour on trust because the auto-approve lane is enforced server-side
 * (see `workers/validate-submission.ts`).
 */
export const authUserSchema = userRefSchema.extend({
    roles: z.array(z.object({
        role: z.string(),
        teamId: z.string().uuid().nullable(),
    })),
    permissions: z.array(z.string()),
});
//# sourceMappingURL=user.js.map